/**
 * File: web/src/routes/Callback.tsx
 * ================================
 *
 * OAuth callback route (Cognito Hosted UI → SPA)
 *
 * Purpose:
 * - Handle the redirect back from Cognito Hosted UI after login.
 * - Exchange the `code` query param for tokens (PKCE code flow).
 * - Store the id_token and navigate the user back to where they started.
 *
 * When used:
 * - Cognito redirects here after a successful sign-in:
 *   https://<siteOrigin>/callback?code=...
 * - Or redirects with an error:
 *   https://<siteOrigin>/callback?error=...
 *
 * Data flow:
 * - Read query params (`code` or `error`)
 * - Call handleCallback(config, code) which:
 *   - validates/verifies PKCE verifier stored in sessionStorage
 *   - POSTs to Cognito /oauth2/token
 *   - stores id_token in sessionStorage
 *   - returns the previously saved post-login path
 * - Navigate to returned destination
 *
 * Gotcha:
 * - If the user refreshes /callback or opens it in a new tab,
 *   the stored PKCE verifier may be missing; handleCallback will throw.
 */

import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import type { AppConfig } from "../lib/types";
import { handleCallback } from "../lib/auth";

export function CallbackRoute(props: { config: AppConfig }) {
  /**
   * useSearchParams() gives us access to the query string.
   * Cognito Hosted UI uses the query string to return:
   * - code=... on success
   * - error=... on failure
   */
  const [params] = useSearchParams();
  /**
   * useNavigate() lets us programmatically send the user to another route
   * once authentication is complete.
   */
  const navigate = useNavigate();

  // UI message shown while we complete authentication.
  const [msg, setMsg] = useState("Working…");

  useEffect(() => {
    /**
     * Step 1: Read values Cognito returned to us.
     * - code: successful auth result (used to fetch tokens)
     * - error: auth failure (user canceled, misconfigured client, etc.)
     */
    const code = params.get("code");
    const err = params.get("error");

    /**
     * If Cognito returned an OAuth error:
     * - We do not attempt token exchange.
     * - We display the error so the user/developer has a clue what happened.
     *
     * (Developers: you may also want to surface `error_description` if present.)
     */
    if (err) {
      setMsg(`OAuth error: ${err}`);
      return;
    }

    /**
     * If we have neither error nor code, the callback URL is malformed.
     * Common reasons:
     * - user navigated here manually
     * - a redirect/callback URL mismatch caused an incomplete redirect
     */
    if (!code) {
      setMsg("Missing authorization code.");
      return;
    }

    /**
     * Step 2: Exchange the authorization code for tokens.
     *
     * We use an async IIFE because useEffect callbacks cannot be async directly.
     * handleCallback() will:
     * - validate we still have the PKCE verifier
     * - POST to /oauth2/token
     * - store id_token in sessionStorage
     * - return the path we saved before login (POST_LOGIN_PATH)
     */
    (async () => {
      try {
        const dest = await handleCallback(props.config, code);

        /**
         * navigate(..., { replace: true }) avoids leaving /callback in browser history.
         * That prevents users from hitting Back and re-triggering the callback logic.
         */
        navigate(dest, { replace: true }); // replace avoids leaving /callback in history
      } catch (e: unknown) {
        /**
         * If token exchange fails, show a useful message.
         * Common causes:
         * - missing PKCE verifier (tab refresh/new tab)
         * - expired/invalid code
         * - mismatched redirect_uri between app and Cognito client settings
         */
        if (e instanceof Error) {
          setMsg(e.message || "Error");
        } else {
          setMsg(String(e) || "Error");
        }
      }
    })();
  }, [navigate, params, props.config]);

  return (
    <main id="main" className="container app-shell" role="main">
      <section className="card">
        <div className="card-header">
          <h1 className="card-title">Signing you in…</h1>
          <p className="card-sub">
            Finishing authentication for WSU Global Campus Secure Upload Portal.
          </p>
        </div>
        <div className="card-body">
          {/* Status message updates as we progress (or fail). */}
          <div className="notice">{msg}</div>
        </div>
      </section>
    </main>
  );
}
